(function () {
  'use strict';

  function visiblePages(current, total, mobile) {
    const count = Math.min(mobile ? 3 : 5, Math.max(0, total));
    const start = Math.max(1, Math.min(current - Math.floor(count / 2), total - count + 1));
    return Array.from({ length: count }, (_, index) => start + index);
  }

  function mediaSize(width, height, limit) {
    const w = Number(width);
    const h = Number(height);
    if (!(w > 0 && h > 0 && Number.isFinite(w) && Number.isFinite(h))) {
      return { width: limit, height: limit };
    }
    const scale = Math.min(1, limit / Math.min(w, h));
    return { width: w * scale, height: h * scale };
  }

  function videoSize(width, height, frameWidth, frameHeight) {
    const w = Number(width);
    const h = Number(height);
    if (!(w > 0 && h > 0 && Number.isFinite(w) && Number.isFinite(h))) {
      return { width: frameWidth, height: frameHeight };
    }
    const ratio = w / h;
    return frameWidth / frameHeight <= ratio
      ? { width: frameWidth, height: frameWidth / ratio }
      : { width: frameHeight * ratio, height: frameHeight };
  }

  function averageText(value) {
    return (Math.max(0, Math.min(5, Number(value) || 0))).toFixed(1);
  }

  function starPercent(count, total) {
    return total > 0 ? Math.max(0, Math.min(100, Math.round(count / total * 1000) / 10)) : 0;
  }

  function readyMedia(items) {
    return (Array.isArray(items) ? items : []).filter((item) =>
      item && item.status === 'READY' && item.url && ['image', 'video'].includes(item.type)
    );
  }

  function dateText(value) {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
    return match ? `${match[1]}/${match[2]}/${match[3]}` : '';
  }

  function mediaKey(item) {
    return JSON.stringify([item.review_id ?? item.review?.id, item.id]);
  }

  function mediaDestination(items, index, step, cursor, current = items[index]) {
    const target = index + step;
    if (index < 0 || ![-1, 1].includes(step) || target < 0) {
      return { action: 'disabled' };
    }
    if (target >= items.length) {
      return { action: cursor ? 'load' : 'disabled' };
    }
    const item = items[target];
    const reviewId = current?.review_id ?? current?.review?.id;
    const sameReview = reviewId != null && reviewId === (item.review_id ?? item.review?.id);
    return { action: sameReview ? 'media' : 'review', index: target, item };
  }

  const pure = {
    visiblePages, mediaSize, videoSize, averageText, starPercent, readyMedia, dateText,
    mediaDestination
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = pure;
  if (typeof document === 'undefined') return;

  // Focus rings are for keyboard users; pointer and touch input hides them (see the CSS).
  const root = document.documentElement;
  document.addEventListener('keydown', (event) => {
    if (!event.metaKey && !event.ctrlKey && !event.altKey) root.dataset.anokInput = 'keyboard';
  }, true);
  document.addEventListener('pointerdown', () => {
    root.dataset.anokInput = 'pointer';
  }, true);

  const prefix = 'anok-product-reviews__';
  function element(tag, name, text) {
    const node = document.createElement(tag);
    if (name) node.className = prefix + name;
    if (text != null) node.textContent = text;
    return node;
  }

  function button(name, text, handler) {
    const node = element('button', name, text);
    node.type = 'button';
    node.addEventListener('click', handler);
    return node;
  }

  function pageArrow(forward, handler) {
    const node = button('page', '', handler);
    node.classList.add(prefix + 'page-arrow');
    const transform = forward ? ' transform="translate(39 0) scale(-1 1)"' : '';
    // W3b's backward chevron, scaled to the pager's 9 x 17 shape in a 40 x 40 frame.
    node.innerHTML = '<svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">' +
      '<path fill="currentColor"' + transform +
      ' d="M21.5 11.5l1.5 1.695-6 6.805 6 6.805-1.5 1.695L14 20z"/></svg>';
    return node;
  }

  // Only public HTTP(S) media URLs may become image/video sources.
  function source(value) {
    try {
      const url = new URL(value);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
    } catch {
      return '';
    }
  }

  function stars(rating) {
    const node = element('span', 'stars');
    node.setAttribute('role', 'img');
    node.setAttribute('aria-label', `5点中${averageText(rating)}点`);
    for (let index = 0; index < 5; index++) {
      const star = element('span', 'star');
      const fill = Math.max(0, Math.min(1, Number(rating) - index)) * 100;
      star.style.setProperty('--fill', `${fill}%`);
      star.setAttribute('aria-hidden', 'true');
      star.innerHTML = '<svg viewBox="0 0 24 24" fill="none">' +
        '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.86L12 17.27' +
        ' 5.82 21 7 14.14 2 9.27l6.91-1.01z"/></svg>';
      node.append(star);
    }
    return node;
  }

  function init(container) {
    if (container.dataset.initialized) return;
    container.dataset.initialized = 'true';
    const find = (selector) => container.querySelector(selector);
    const average = find('[data-summary-average]');
    const distribution = find('[data-summary-distribution]');
    const reviewsList = find('[data-reviews-list]');
    const pagination = find('[data-pagination]');
    const sort = find('[data-sort-select]');
    const featured = find('[data-featured-media]');
    const dialog = find('[data-media-dialog]');
    const scroller = find('[data-gallery-scroll]');
    const listView = find('[data-gallery-list]');
    const detail = find('[data-gallery-detail]');
    const grid = find('[data-gallery-grid]');
    const status = find('[data-gallery-status]');
    const load = find('[data-gallery-load]');
    const back = find('[data-gallery-back]');
    const close = find('[data-gallery-close]');
    const mobile = window.matchMedia('(max-width: 749px)');
    const perPage = Math.min(5, Math.max(1, Number(container.dataset.perPage) || 5));
    let page = 1;
    let totalPages = 1;
    let reviewRequest = 0;
    let galleryRequest = 0;
    let filter = 'all';
    let cursor = null;
    let galleryStarted = false;
    let galleryBusy = false;
    let galleryFailed = false;
    let opener;
    let listOpener;
    let listScroll = 0;
    let bodyOverflow;
    let htmlOverflow;
    let bodyPadding;
    let observer;
    const seen = new Set();
    let galleryItems = [];
    let viewer = null;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    async function get(route, params = {}) {
      const query = new URLSearchParams({ product_id: container.dataset.productId, ...params });
      const response = await fetch(`/apps/features/review/v2/${route}?${query}`);
      if (!response.ok) throw new Error('Review request failed');
      return response.json();
    }

    function thumbnail(item, review, inline = false) {
      const node = button('thumbnail', '', () => {
        if (node.closest('.' + prefix + 'related')) selectRelated(item, review);
        else showDetail(item, review, node);
      });
      node.dataset.mediaId = item.id;
      node.setAttribute('aria-label', `${item.type === 'video' ? '動画' : '写真'}を拡大表示`);
      const url = source(item.type === 'video' ? item.preview?.url : item.url);
      if (url) {
        const img = element('img');
        img.src = url;
        img.alt = '';
        img.loading = 'lazy';
        node.append(img);
      }
      if (item.type === 'video') {
        const play = element('span', 'play');
        play.innerHTML = '<svg viewBox="0 0 6 8" preserveAspectRatio="none" focusable="false">' +
          '<path fill="currentColor" d="M0 0l6 4-6 4z"/></svg>';
        play.setAttribute('aria-hidden', 'true');
        node.append(play);
      }
      if (inline) {
        node.classList.add(prefix + 'thumbnail--inline');
        for (const [name, limit] of [['pc', 160], ['sp', 120]]) {
          const size = mediaSize(item.width, item.height, limit);
          node.style.setProperty(`--${name}-width`, `${size.width}px`);
          node.style.setProperty(`--${name}-height`, `${size.height}px`);
        }
      }
      return node;
    }

    function reviewCard(review, inDetail = false, selected = null) {
      const card = element('article', 'card');
      const meta = element('div', 'meta');
      const author = element('div', 'author');
      const date = element('time', '', dateText(review.date));
      if (date.textContent) date.dateTime = review.date.substring(0, 10);
      author.append(date);
      if (review.nickname) author.append(element('span', '', review.nickname));
      meta.append(stars(review.rating), author);
      card.append(meta);
      if (review.title) card.append(element('h4', 'card-title', review.title));
      card.append(element('p', 'body', review.body));
      const media = readyMedia(review.media);
      if (media.length) {
        const strip = element('div', inDetail ? 'related' : 'attachments');
        media.forEach((item) => {
          const thumb = thumbnail(item, review, !inDetail);
          if (inDetail) thumb.setAttribute('aria-pressed', String(item.id === selected));
          strip.append(thumb);
        });
        card.append(strip);
      }
      return card;
    }

    async function summary() {
      try {
        const data = await get('summary');
        average.replaceChildren();
        distribution.replaceChildren();
        if (!data.count) {
          average.textContent = 'まだレビューがありません';
          return;
        }
        average.append(element('div', 'score', averageText(data.average)), stars(data.average));
        average.append(element('div', 'count', `${data.count}件のレビュー`));
        for (let rating = 5; rating >= 1; rating--) {
          const count = data[`star${rating}`] || 0;
          const percent = starPercent(count, data.count);
          const row = element('div', 'distribution-row');
          const track = element('span', 'bar');
          const fill = element('span', 'bar-fill');
          fill.style.width = `${percent}%`;
          track.setAttribute('aria-hidden', 'true');
          track.append(fill);
          row.append(stars(rating), track, element('span', 'percent', `${percent}%`));
          row.append(element('span', 'rating-count', `(${count})`));
          distribution.append(row);
        }
      } catch {
        average.textContent = 'レビュー情報を読み込めませんでした';
      }
    }

    function renderPagination() {
      pagination.replaceChildren();
      if (totalPages <= 1) return;
      const previous = pageArrow(false, () => fetchReviews(page - 1, true));
      previous.setAttribute('aria-label', '前のページ');
      previous.disabled = page <= 1;
      const numbers = element('div', 'page-numbers');
      visiblePages(page, totalPages, mobile.matches).forEach((number) => {
        const node = number === page ? element('span', 'page', number) :
          button('page', number, () => fetchReviews(number, true));
        if (number === page) node.setAttribute('aria-current', 'page');
        else node.setAttribute('aria-label', `${number}ページ目`);
        numbers.append(node);
      });
      const next = pageArrow(true, () => fetchReviews(page + 1, true));
      next.setAttribute('aria-label', '次のページ');
      next.disabled = page >= totalPages;
      pagination.append(previous, numbers, next);
    }

    async function fetchReviews(target, move = false) {
      const request = ++reviewRequest;
      reviewsList.setAttribute('aria-busy', 'true');
      try {
        const data = await get('reviews', { page: target, per_page: perPage, sort: sort.value });
        if (request !== reviewRequest) return;
        page = data.page;
        totalPages = data.total_pages;
        reviewsList.replaceChildren(...data.reviews.map((review) => reviewCard(review)));
        if (!data.total) reviewsList.textContent = 'まだレビューがありません';
        renderPagination();
        if (move) {
          reviewsList.tabIndex = -1;
          reviewsList.focus({ preventScroll: true });
          reviewsList.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch {
        if (request !== reviewRequest) return;
        reviewsList.replaceChildren(element('p', '', 'レビューを読み込めませんでした'));
        reviewsList.append(button('retry', '再試行', () => fetchReviews(target, move)));
        pagination.replaceChildren();
      } finally {
        if (request === reviewRequest) reviewsList.setAttribute('aria-busy', 'false');
      }
    }

    async function fetchFeatured() {
      const items = [];
      let next;
      try {
        do {
          const params = { type: 'all', limit: 20 };
          if (next) params.cursor = next;
          const data = await get('media', params);
          items.push(...readyMedia(data.media));
          next = data.next_cursor;
        } while (items.length < 4 && next);
        featured.replaceChildren(...items.slice(0, 4).map((item) =>
          thumbnail(item, item.review)
        ));
        find('[data-media-section]').hidden = !items.length;
      } catch {
        // An unavailable media feed must not hide the text reviews.
        featured.replaceChildren();
        find('[data-media-section]').hidden = true;
      }
    }

    let cancelDialogClose;
    const openingClass = prefix + 'dialog--opening';
    const closingClass = prefix + 'dialog--closing';

    function resetDialogMotion() {
      cancelDialogClose?.();
      cancelDialogClose = null;
      dialog.classList.remove(openingClass, closingClass);
    }

    function openDialog(trigger) {
      if (dialog.open) {
        if (cancelDialogClose) resetDialogMotion();
        return;
      }
      resetDialogMotion();
      opener = trigger;
      bodyOverflow = document.body.style.overflow;
      htmlOverflow = document.documentElement.style.overflow;
      bodyPadding = document.body.style.paddingRight;
      // Fill the page scrollbar's width while locked so the page does not shift sideways.
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      if (!reducedMotion.matches) dialog.classList.add(openingClass);
      dialog.showModal();
    }

    const stageVideos = new WeakMap();
    const videoFrames = new ResizeObserver((entries) => {
      for (const { target, contentRect } of entries) {
        stageVideos.get(target)?.resize(contentRect.width, contentRect.height);
      }
    });

    function stopVideo() {
      detail.querySelectorAll('video').forEach((video) => {
        const stage = video.parentElement;
        stageVideos.get(stage)?.reset();
        videoFrames.unobserve(stage);
      });
    }

    function closeDialog(immediate = false) {
      if (!dialog.open) return;
      if (immediate === true || reducedMotion.matches) {
        resetDialogMotion();
        dialog.close();
        return;
      }
      if (cancelDialogClose) return;
      dialog.classList.remove(openingClass);
      dialog.classList.add(closingClass);
      const finish = () => {
        resetDialogMotion();
        if (dialog.open) dialog.close();
      };
      const onEnd = (event) => {
        if (event.target === dialog && !event.pseudoElement &&
            event.animationName === 'anok-dialog-out') finish();
      };
      const duration = getComputedStyle(dialog).animationDuration;
      const milliseconds = parseFloat(duration) * (duration.endsWith('ms') ? 1 : 1000);
      const timer = setTimeout(finish, (milliseconds || 0) + 100);
      dialog.addEventListener('animationend', onEnd);
      cancelDialogClose = () => {
        clearTimeout(timer);
        dialog.removeEventListener('animationend', onEnd);
      };
    }

    dialog.addEventListener('close', () => {
      resetDialogMotion();
      stopVideo();
      viewer = null;
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
      document.body.style.paddingRight = bodyPadding;
      if (opener?.isConnected) opener.focus();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    dialog.addEventListener('keydown', (event) => {
      if (viewer && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
        navigateMedia(event.key === 'ArrowLeft' ? -1 : 1);
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = Array.from(dialog.querySelectorAll(
        'button:not(:disabled), a[href], video[controls], [tabindex="0"]'
      )).filter((node) => node.getClientRects().length);
      const first = nodes[0];
      const last = nodes.at(-1);
      const atStart = document.activeElement === first || document.activeElement === dialog;
      if (event.shiftKey && atStart) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    });

    function mediaStage(item, review) {
      const stage = element('div', 'stage');
      const media = element(item.type === 'video' ? 'video' : 'img');
      media.src = source(item.url);
      if (item.type === 'video') {
        stage.classList.add(prefix + 'stage--video');
        media.playsInline = true;
        media.preload = 'metadata';
        if (source(item.preview?.url)) media.poster = source(item.preview.url);
        let playRequest = 0;
        const reset = () => {
          playRequest++;
          const restoreFocus = document.activeElement === media;
          const wasStarted = media.controls || media.currentTime !== 0;
          media.pause();
          media.currentTime = 0;
          media.controls = false;
          play.hidden = false;
          // Seeking alone leaves the last decoded frame visible instead of the poster.
          if (wasStarted) media.load();
          if (restoreFocus) play.focus({ preventScroll: true });
        };
        const play = button('stage-play', '', async () => {
          const request = ++playRequest;
          play.hidden = true;
          media.controls = true;
          media.focus({ preventScroll: true });
          try {
            await media.play();
          } catch {
            // A stopped or replaced video's old promise must not reset a new attempt.
            if (request === playRequest) reset();
          }
        });
        play.setAttribute('aria-label', '動画を再生');
        play.innerHTML = '<svg viewBox="0 0 11 14" preserveAspectRatio="none"' +
          ' aria-hidden="true" focusable="false">' +
          '<path fill="currentColor" d="M0 0l11 7-11 7z"/></svg>';
        media.addEventListener('ended', reset);
        stageVideos.set(stage, {
          reset,
          resize(width, height) {
            const size = videoSize(item.width, item.height, width, height);
            media.style.width = `${size.width}px`;
            media.style.height = `${size.height}px`;
          }
        });
        videoFrames.observe(stage);
        stage.append(play);
      } else {
        media.alt = review.title || 'レビューの写真';
      }
      stage.append(media);
      return stage;
    }

    function detailSlide(item) {
      const slide = element('div', 'slide');
      slide.append(mediaStage(item, item.review), reviewCard(item.review, true, item.id));
      return slide;
    }

    function updateArrows(session) {
      if (viewer !== session) return;
      for (const [step, arrow] of session.arrows) {
        const destination = mediaDestination(
          session.items, session.index, step, session.cursor, session.current
        );
        arrow.disabled = session.busy || destination.action === 'disabled';
      }
      detail.setAttribute('aria-busy', String(session.busy));
    }

    async function readViewerPage(session) {
      const params = { type: session.type, limit: 20 };
      if (session.cursor) params.cursor = session.cursor;
      const data = await get('media', params);
      if (viewer !== session) return;
      if (data.next_cursor && data.next_cursor === session.cursor) {
        throw new Error('Media cursor did not advance');
      }
      const keys = new Set(session.items.map(mediaKey));
      readyMedia(data.media).forEach((item) => {
        if (!item.review || keys.has(mediaKey(item))) return;
        keys.add(mediaKey(item));
        session.items.push(item);
      });
      session.cursor = data.next_cursor;
    }

    async function locateCurrent(session) {
      do {
        session.index = session.items.findIndex((item) =>
          mediaKey(item) === mediaKey(session.current)
        );
        if (session.index >= 0) return;
        if (session.started && !session.cursor) break;
        await readViewerPage(session);
        session.started = true;
      } while (viewer === session);
      if (viewer === session) throw new Error('Media is no longer listed');
    }

    async function prepareViewer(session) {
      session.busy = true;
      updateArrows(session);
      try {
        await locateCurrent(session);
        if (viewer === session) session.message.textContent = '';
      } catch {
        if (viewer !== session) return;
        session.message.replaceChildren(element('span', '', '一覧を読み込めませんでした。'));
        session.message.append(button('retry', '再試行', () => prepareViewer(session)));
      } finally {
        session.busy = false;
        updateArrows(session);
      }
    }

    function showDetail(item, review, trigger) {
      if (!readyMedia([item]).length || !review) return;
      const fromList = dialog.open && !listView.hidden;
      if (fromList) {
        listScroll = scroller.scrollTop;
        listOpener = trigger;
      }
      openDialog(trigger);
      stopVideo();
      listView.hidden = true;
      detail.hidden = false;
      back.hidden = false;
      dialog.classList.add(prefix + 'dialog--detail');
      const current = { ...item, review_id: review.id, review };
      const session = {
        current, items: fromList ? galleryItems.slice() : [], index: -1,
        type: fromList ? filter : 'all', cursor: fromList ? cursor : null,
        started: fromList, busy: false, arrows: [],
        slides: element('div', 'slides'), message: element('p', 'viewer-status')
      };
      viewer = session;
      session.message.setAttribute('role', 'status');
      session.slides.append(detailSlide(current));
      detail.replaceChildren(session.slides, session.message);
      for (const [step, label] of [[-1, '前の添付'], [1, '次の添付']]) {
        const arrow = button(step < 0 ? 'media-prev' : 'media-next', '', () => {
          navigateMedia(step);
        });
        arrow.setAttribute('aria-label', label);
        arrow.innerHTML = '<svg viewBox="0 0 32 32" aria-hidden="true">' +
          '<path fill="currentColor" d="M18 8.9l1.4 1.4-5.7 5.7 5.7 5.7-1.4 1.4' +
          '-7.1-7.1z"/></svg>';
        session.arrows.push([step, arrow]);
        detail.append(arrow);
      }
      scroller.scrollTop = 0;
      back.focus();
      prepareViewer(session);
    }

    function replaceMedia(session, item) {
      stopVideo();
      const slide = session.slides.firstElementChild;
      slide.firstElementChild.replaceWith(mediaStage(item, item.review));
      slide.querySelectorAll('[aria-pressed]').forEach((thumb) => {
        thumb.setAttribute('aria-pressed', String(thumb.dataset.mediaId === String(item.id)));
      });
      session.current = item;
    }

    function selectRelated(item, review) {
      const session = viewer;
      if (!session || session.busy) return;
      const selected = { ...item, review_id: review.id, review };
      replaceMedia(session, selected);
      const index = session.items.findIndex((entry) => mediaKey(entry) === mediaKey(selected));
      // A thumbnail outside the active filter leaves the list position unchanged.
      if (index >= 0) session.index = index;
      else if (session.type === 'all' || session.type === item.type) prepareViewer(session);
      updateArrows(session);
    }

    async function navigateMedia(step) {
      const session = viewer;
      if (!session || session.busy) return;
      const focus = document.activeElement;
      session.busy = true;
      session.message.textContent = '';
      updateArrows(session);
      try {
        let destination;
        do {
          destination = mediaDestination(
            session.items, session.index, step, session.cursor, session.current
          );
          if (destination.action !== 'load') break;
          session.message.textContent = '読み込み中...';
          await readViewerPage(session);
          if (viewer !== session) return;
        } while (true);
        session.message.textContent = '';
        if (destination.action === 'disabled') return;
        stopVideo();
        session.index = destination.index;
        if (destination.action === 'media') {
          replaceMedia(session, destination.item);
          return;
        }
        const outgoing = session.slides.firstElementChild;
        const incoming = detailSlide(destination.item);
        session.current = destination.item;
        scroller.scrollTop = 0;
        if (reducedMotion.matches || !incoming.animate) {
          session.slides.replaceChildren(incoming);
          return;
        }
        outgoing.inert = true;
        outgoing.classList.add(prefix + 'slide--outgoing');
        session.slides.append(incoming);
        const options = { duration: 240, easing: 'ease-in-out' };
        const animations = [
          outgoing.animate([
            { transform: 'translateX(0)' }, { transform: `translateX(${-step * 100}%)` }
          ], options),
          incoming.animate([
            { transform: `translateX(${step * 100}%)` }, { transform: 'translateX(0)' }
          ], options)
        ];
        await Promise.all(animations.map((animation) => animation.finished.catch(() => {})));
        outgoing.remove();
      } catch {
        if (viewer === session) {
          session.message.textContent = '続きを読み込めませんでした。矢印で再試行できます。';
        }
      } finally {
        session.busy = false;
        updateArrows(session);
        if (viewer === session && (!focus.isConnected || focus.inert ||
            document.activeElement === document.body)) {
          const arrow = session.arrows.find(([direction, node]) =>
            direction === step && !node.disabled
          ) || session.arrows.find(([, node]) => !node.disabled);
          (arrow?.[1] || back).focus({ preventScroll: true });
        }
      }
    }

    function showList(trigger) {
      viewer = null;
      openDialog(trigger);
      stopVideo();
      detail.hidden = true;
      detail.replaceChildren();
      listView.hidden = false;
      back.hidden = true;
      dialog.classList.remove(prefix + 'dialog--detail');
      scroller.scrollTop = listScroll;
      if (listOpener?.isConnected) listOpener.focus();
      else close.focus();
      if (!galleryStarted) resetGallery();
      else checkEnd();
    }

    function resetGallery() {
      galleryRequest++;
      galleryStarted = true;
      galleryBusy = false;
      galleryFailed = false;
      cursor = null;
      listScroll = 0;
      listOpener = null;
      seen.clear();
      galleryItems = [];
      grid.replaceChildren();
      scroller.scrollTop = 0;
      container.querySelectorAll('[data-filter]').forEach((node) => {
        node.setAttribute('aria-pressed', String(node.dataset.filter === filter));
      });
      fetchGallery();
    }

    async function fetchGallery() {
      if (galleryBusy) return;
      const request = galleryRequest;
      galleryBusy = true;
      galleryFailed = false;
      load.hidden = true;
      status.textContent = '読み込み中...';
      grid.setAttribute('aria-busy', 'true');
      try {
        const params = { type: filter, limit: 20 };
        if (cursor) params.cursor = cursor;
        const data = await get('media', params);
        if (request !== galleryRequest) return;
        readyMedia(data.media).forEach((item) => {
          const key = JSON.stringify([item.review_id, item.id]);
          if (seen.has(key)) return;
          seen.add(key);
          galleryItems.push(item);
          grid.append(thumbnail(item, item.review));
        });
        cursor = data.next_cursor;
        status.textContent = seen.size ? '' : (cursor ? '読み込み中...' : '写真・動画はありません');
        load.textContent = '続きを読み込む';
        load.hidden = !cursor;
      } catch {
        if (request !== galleryRequest) return;
        galleryFailed = true;
        status.textContent = '写真・動画を読み込めませんでした';
        load.textContent = '再試行';
        load.hidden = false;
      } finally {
        if (request === galleryRequest) {
          galleryBusy = false;
          grid.setAttribute('aria-busy', 'false');
          requestAnimationFrame(checkEnd);
        }
      }
    }

    function checkEnd() {
      if (!dialog.open || listView.hidden || galleryBusy || galleryFailed || !cursor) return;
      if (scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 160) {
        fetchGallery();
      }
    }

    sort.addEventListener('change', () => fetchReviews(1));
    mobile.addEventListener('change', renderPagination);
    find('[data-gallery-open]').addEventListener('click', (event) => {
      listScroll = 0;
      listOpener = null;
      showList(event.currentTarget);
    });
    back.addEventListener('click', () => showList(opener));
    close.addEventListener('click', closeDialog);
    load.addEventListener('click', fetchGallery);
    scroller.addEventListener('scroll', checkEnd, { passive: true });
    container.querySelectorAll('[data-filter]').forEach((node) => {
      node.addEventListener('click', () => {
        filter = node.dataset.filter;
        resetGallery();
      });
    });
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(checkEnd, { root: scroller, rootMargin: '160px' });
      observer.observe(load);
    }
    // Theme editor unload must release the native dialog and its scroll lock.
    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(container)) return;
      closeDialog(true);
      observer?.disconnect();
      mobile.removeEventListener('change', renderPagination);
    });
    summary();
    fetchReviews(1);
    fetchFeatured();
  }

  function boot(root) {
    root.querySelectorAll('[data-reviews-section]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
